import { useState, useEffect, useRef } from "react";
import { IoAddOutline, IoPeople, IoSearchOutline } from "react-icons/io5";

import { useDispatch, useSelector } from "react-redux";
import useGetConversations from "../../hooks/useGetConversations";
import { setSelectedConversation } from "../../Slices/ChatSlice";
import CreateGroupModal from "./CreateGroupModal";

const SearchInput = () => {
  const dispatch = useDispatch();
  const Api_URL = process.env.REACT_APP_URL;

  const { loading } = useGetConversations(false);
  const currentUser = useSelector(
    (state) => state?.data?.userInfo?.data?.data?.UserId
  );
  const usersData = useSelector((state) => state?.data?.practice?.users?.value);

  const conversationsData = usersData?.filter(
    (item) => item?.value !== currentUser
  );

  const [filteredConversations, setFilteredConversations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [placeholder, setPlaceholder] = useState("Search here");
  const [showMenu, setShowMenu] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);

  const menuRef = useRef(null);
  const ellipsisRef = useRef(null);
  const inputRef = useRef(null);

  const handleSelect = (conversation) => {
    setSearchTerm(conversation.label);
    dispatch(setSelectedConversation({ conversation, type: "simple" }));
    setShowSuggestions(false);
  };

  const handleSearch = (event) => {
    const input = event.target.value;
    setSearchTerm(input.trimStart());

    const searchTerm = input.trim().toLowerCase();
    if (searchTerm.length >= 2) {
      const filtered = conversationsData.filter((conversation) => {
        const normalizedLabel = conversation.label.toLowerCase().trim();
        const labelParts = normalizedLabel.split(" ").filter(Boolean);

        return (
          normalizedLabel.startsWith(searchTerm) ||
          labelParts.some((part) => part.startsWith(searchTerm)) ||
          normalizedLabel.includes(searchTerm)
        );
      });

      setFilteredConversations(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredConversations([]);
      setShowSuggestions(false);
    }
  };

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleCreateGroupClick = () => {
    setShowCreateGroupModal(true);
    setShowMenu(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close menu when clicking outside
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        ellipsisRef.current &&
        !ellipsisRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }

      // Close suggestions when clicking outside
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="px-4 mb-2 z-20">
      <div className="relative flex items-center drop-shadow-md">
        {/* Search Input */}
        <div className="relative flex-1" ref={inputRef}>
          <div className="relative">
            <IoSearchOutline className="absolute font-extrabold left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="search"
              placeholder={placeholder}
              className="w-full py-2.5 pl-10 pr-3 rounded-full bg-blue-50 text-gray-700 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all"
              value={searchTerm}
              onChange={handleSearch}
              onFocus={() => setPlaceholder("Type at least two letters")}
              onBlur={() =>
                setTimeout(() => setPlaceholder("Search here"), 200)
              }
            />
          </div>

          {/* Search Suggestions */}
          {showSuggestions && (
            <ul className="absolute top-full left-0 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-60 overflow-auto shadow-lg ">
              {filteredConversations.length > 0 ? (
                filteredConversations.map((conversation) => (
                  <li
                    key={conversation._id}
                    className="p-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleSelect(conversation)}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-800 truncate">
                        {conversation.label}
                      </span>
                      <span className="text-xs text-gray-500 truncate">
                        {conversation.email}
                      </span>
                    </div>
                  </li>
                ))
              ) : (
                <li className="p-3 text-gray-500 text-center">
                  No results found
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Menu Button */}
        <div className="relative ml-2" ref={ellipsisRef}>
          <button
            className="p-1.5 rounded-full hover:bg-gray-200 transition-colors"
            onClick={handleCreateGroupClick}
            aria-label="Creat Group"
            title="Creat Group"
          >
            <IoAddOutline className="w-5 h-5 text-gray-600" />
          </button>

          {/* Dropdown Menu */}
          {showMenu && (
            <div
              className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
              ref={menuRef}
            >
              <ul>
                <li>
                  <button
                    className="w-full text-left px-4 py-2.5 flex items-center gap-2.5 hover:bg-gray-50 transition-colors text-gray-700"
                    onClick={handleCreateGroupClick}
                  >
                    <IoPeople className="w-5 h-5 text-blue-500" />
                    <span>Create New Group</span>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Group Modal */}
      <CreateGroupModal
        showCreateGroupModal={showCreateGroupModal}
        setShowCreateGroupModal={setShowCreateGroupModal}
        currentUser={currentUser}
        conversationsData={conversationsData}
        Api_URL={Api_URL}
      />
    </div>
  );
};

export default SearchInput;
